import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Loader2, ShieldCheck, Sparkles, Receipt, FileText, ScanText, UserX, Lock,
  Music as MusicIcon, Upload, Trash2, Volume2, Check, X, Crown, Tv, AlertTriangle, Radio,
} from "lucide-react";
import { eventConfigApi, musicApi, spotifyApi, tvApi, formatDate, getFileUrl } from "../lib/api";
import { toast } from "sonner";

// Order matches the labels we want to display
const RULE_DEFS = [
  {
    key: "allow_no_face",
    icon: UserX,
    title: "Aceitar fotos sem rosto",
    desc: "Por padrao bloqueamos fotos sem pessoas/animais. Ative se quiser permitir paisagens, comida e decoracao tambem.",
  },
  {
    key: "allow_documents",
    icon: FileText,
    title: "Aceitar documentos / prints",
    desc: "Permite envio de RG, CPF, recibos, prints de tela. Geralmente nao desejavel em festas.",
  },
  {
    key: "allow_text_only",
    icon: ScanText,
    title: "Aceitar imagens so com texto",
    desc: "Permite memes ou imagens contendo apenas texto.",
  },
];

export default function HostSettingsDialog({ open, onOpenChange, eventId, eventName }) {
  const [tab, setTab] = useState("rules"); // 'rules' | 'payments'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState({
    allow_documents: false,
    allow_no_face: false,
    allow_text_only: false,
  });
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    if (!open || !eventId) return;
    setLoading(true);
    Promise.all([eventConfigApi.getRules(eventId), eventConfigApi.getPayments(eventId)])
      .then(([s, p]) => {
        setRules({
          allow_documents: !!s.data?.content_rules?.allow_documents,
          allow_no_face: !!s.data?.content_rules?.allow_no_face,
          allow_text_only: !!s.data?.content_rules?.allow_text_only,
        });
        setPayments(p.data || []);
      })
      .catch((e) => {
        console.error(e);
        toast.error("Erro ao carregar configuracoes.");
      })
      .finally(() => setLoading(false));
  }, [open, eventId]);

  const save = async () => {
    setSaving(true);
    try {
      await eventConfigApi.updateRules(eventId, rules);
      toast.success("Configuracoes salvas!");
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      toast.error("Nao foi possivel salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl w-[calc(100vw-2rem)] max-w-lg max-h-[85vh] p-0 overflow-hidden flex flex-col"
        data-testid="host-settings-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 shrink-0">
          <DialogTitle
            className="text-lg sm:text-xl font-black flex items-center gap-2"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            <Sparkles className="w-5 h-5 text-[#FF6B6B] shrink-0" strokeWidth={2.5} />
            <span className="truncate">Configuracoes</span>
          </DialogTitle>
          {eventName && (
            <p className="text-xs text-[#78716C] truncate">
              Evento: <span className="font-bold text-[#1C1917]">{eventName}</span>
            </p>
          )}
        </DialogHeader>

        {/* Tabs — horizontal scroll on small screens to avoid wrapping */}
        <div
          className="flex gap-1 border-b-2 border-[#1C1917] px-3 shrink-0 overflow-x-auto scrollbar-hide"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {[
            { id: "rules", label: "Conteudo", icon: ShieldCheck },
            { id: "music", label: "Musica", icon: MusicIcon },
            { id: "livetv", label: "TV ao vivo", icon: Radio },
            { id: "tvs", label: "TVs", icon: Tv },
            { id: "payments", label: "Pagamentos", icon: Receipt },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`shrink-0 flex items-center gap-1.5 text-xs sm:text-sm font-bold py-2.5 px-2.5 sm:px-3 -mb-[2px] border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id
                  ? "border-[#FF6B6B] text-[#1C1917]"
                  : "border-transparent text-[#78716C] hover:text-[#1C1917]"
              }`}
              style={{ scrollSnapAlign: "start" }}
              data-testid={`settings-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4 shrink-0" strokeWidth={2.5} />
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-[#FF6B6B]" />
            </div>
          ) : tab === "rules" ? (
            <RulesPanel rules={rules} setRules={setRules} saving={saving} onSave={save} />
          ) : tab === "music" ? (
            <MusicPanel eventId={eventId} />
          ) : tab === "livetv" ? (
            <LiveTvPanel eventId={eventId} />
          ) : tab === "tvs" ? (
            <TVDevicesPanel eventId={eventId} />
          ) : (
            <PaymentsPanel payments={payments} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RulesPanel({ rules, setRules, saving, onSave }) {
  return (
    <div className="space-y-3">
      <p className="text-[12px] text-[#78716C] leading-snug">
        Por padrao, so aceitamos fotos com pessoas ou animais. Ative as opcoes abaixo para
        relaxar essas regras no seu evento.
      </p>

      {RULE_DEFS.map((def) => (
        <ToggleRow
          key={def.key}
          icon={def.icon}
          title={def.title}
          desc={def.desc}
          checked={rules[def.key]}
          onChange={(v) => setRules((r) => ({ ...r, [def.key]: v }))}
          testId={`rule-${def.key}`}
        />
      ))}

      {/* Sem restricao - permanently disabled per app ethics */}
      <ToggleRow
        icon={Lock}
        title="Sem restricao (modo livre)"
        desc="Indisponivel: nao removemos a moderacao de pornografia, violencia, drogas, racismo e discurso de odio. Essas regras sao permanentes."
        checked={false}
        disabled
        testId="rule-unrestricted"
      />

      <button
        onClick={onSave}
        disabled={saving}
        className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all disabled:opacity-60"
        data-testid="save-settings-btn"
      >
        {saving ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Salvar"}
      </button>
    </div>
  );
}

function ToggleRow({ icon: Icon, title, desc, checked, onChange, disabled, testId }) {
  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onChange(!checked);
  };
  return (
    <label
      onClick={handleClick}
      className={`flex items-start gap-3 bg-white border-2 border-[#1C1917] rounded-lg p-3 shadow-[2px_2px_0px_#1C1917] select-none ${
        disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
      }`}
      data-testid={testId}
    >
      <div className="w-8 h-8 bg-[#1C1917] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-bold text-[#1C1917] leading-tight">{title}</p>
        <p className="text-[11px] text-[#78716C] leading-snug mt-0.5">{desc}</p>
      </div>
      <span
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-[#1C1917] mt-0.5 transition-colors shadow-[1px_1px_0px_#1C1917] ${
          disabled ? "bg-[#E7E5E4]" : checked ? "bg-[#FF6B6B]" : "bg-[#E7E5E4]"
        }`}
        aria-hidden="true"
      >
        <span
          className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white border-2 border-[#1C1917] transition-transform ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </span>
    </label>
  );
}

function PaymentsPanel({ payments }) {
  if (payments.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-[#78716C]">
        <Receipt className="w-8 h-8 mx-auto mb-2 text-[#E7E5E4]" strokeWidth={2.5} />
        Nenhum pagamento encontrado para este evento.
      </div>
    );
  }
  return (
    <div className="space-y-2 max-h-[60vh] overflow-y-auto">
      {payments.map((tx, i) => (
        <div
          key={tx.transaction_id || i}
          className="bg-white border-2 border-[#1C1917] rounded-lg p-3 shadow-[2px_2px_0px_#1C1917]"
          data-testid={`payment-row-${tx.transaction_id || i}`}
        >
          <div className="flex items-center justify-between mb-1">
            <p className="text-[13px] font-bold text-[#1C1917] flex-1">
              R$ {Number(tx.amount || 0).toFixed(2).replace(".", ",")}{" "}
              {tx.currency ? <span className="uppercase text-[10px] text-[#78716C]">{tx.currency}</span> : null}
            </p>
            <span
              className={`shrink-0 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border-2 border-[#1C1917] ${
                tx.payment_status === "paid"
                  ? "bg-green-100 text-green-800"
                  : tx.payment_status === "pending"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {tx.payment_status || "?"}
            </span>
          </div>
          <div className="flex items-center justify-between text-[11px] text-[#78716C]">
            <span>{tx.session_id ? `Sessao: ${tx.session_id.slice(0, 16)}...` : ""}</span>
            <span>{tx.created_at ? formatDate(tx.created_at.slice(0, 10)) : ""}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function MusicPanel({ eventId }) {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  const MAX = 10;

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await musicApi.list(eventId);
      setTracks(data || []);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao carregar a playlist.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    const remaining = MAX - tracks.length;
    if (remaining <= 0) {
      toast.error(`Limite de ${MAX} musicas atingido. Remova uma para enviar outra.`);
      return;
    }
    const toUpload = list.slice(0, remaining);
    if (toUpload.length < list.length) {
      toast.message(`Voce so pode adicionar ${remaining} musica(s). Os primeiros ${remaining} arquivos serao enviados.`);
    }
    setUploading(true);
    try {
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          await musicApi.upload(eventId, fd, (e) => {
            if (e?.total) setProgress(Math.round((e.loaded / e.total) * 100));
          });
        } catch (err) {
          const msg = err?.response?.data?.detail || "Erro ao enviar musica.";
          toast.error(`${file.name}: ${msg}`);
        }
      }
      await reload();
      toast.success("Playlist atualizada!");
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeTrack = async (trackId) => {
    try {
      await musicApi.remove(eventId, trackId);
      setTracks((prev) => prev.filter((t) => t.track_id !== trackId));
      toast.success("Musica removida.");
    } catch (e) {
      console.error(e);
      toast.error("Nao foi possivel remover.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF6B6B]" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Spotify connection — alternative to MP3 upload */}
      <SpotifyPanel eventId={eventId} />

      <div className="flex items-start gap-2 bg-[#FEF3C7] border-2 border-[#1C1917] rounded-lg p-3 shadow-[2px_2px_0px_#1C1917]">
        <Volume2 className="w-4 h-4 text-[#B45309] shrink-0 mt-0.5" strokeWidth={2.5} />
        <p className="text-[11px] text-[#78716C] leading-snug">
          Ou adicione ate <span className="font-bold text-[#1C1917]">{MAX} musicas</span> proprias (MP3/WAV/M4A, max 15 MB cada). A
          playlist toca em loop no Modo TV. Quando aparecer um video, a musica pausa automaticamente
          e volta no proximo slide de foto.
        </p>
      </div>

      {tracks.length > 0 && (
        <div className="space-y-2 max-h-[40vh] overflow-y-auto" data-testid="music-tracks-list">
          {tracks.map((t, i) => (
            <div
              key={t.track_id}
              className="flex items-center gap-3 bg-white border-2 border-[#1C1917] rounded-lg p-2.5 shadow-[2px_2px_0px_#1C1917]"
              data-testid={`music-track-${t.track_id}`}
            >
              <div className="w-8 h-8 bg-[#1C1917] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shrink-0">
                <span className="text-white text-[11px] font-black">{i + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold text-[#1C1917] truncate" title={t.name}>
                  {t.name}
                </p>
                <audio
                  src={getFileUrl(t.storage_path)}
                  controls
                  preload="none"
                  className="w-full h-7 mt-1"
                />
              </div>
              <button
                onClick={() => removeTrack(t.track_id)}
                className="w-8 h-8 bg-white border-2 border-[#1C1917] rounded-lg flex items-center justify-center text-red-600 hover:bg-red-50 shadow-[1px_1px_0px_#1C1917] shrink-0"
                aria-label={`Remover ${t.name}`}
                data-testid={`remove-track-${t.track_id}`}
              >
                <Trash2 className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="text-[11px] text-[#78716C] text-right">
        {tracks.length} de {MAX}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="music-upload-input"
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading || tracks.length >= MAX}
        className="w-full flex items-center justify-center gap-2 bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all disabled:opacity-60"
        data-testid="add-music-btn"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Enviando... {progress}%
          </>
        ) : (
          <>
            <Upload className="w-4 h-4" strokeWidth={2.5} />
            {tracks.length === 0 ? "Adicionar musicas" : "Adicionar mais musicas"}
          </>
        )}
      </button>
    </div>
  );
}


function SpotifyPanel({ eventId }) {
  const [status, setStatus] = useState(null); // null=loading
  const [picking, setPicking] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [loadingPl, setLoadingPl] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await spotifyApi.status(eventId);
      setStatus(data);
    } catch (e) {
      setStatus({ connected: false });
    }
  };

  useEffect(() => {
    if (eventId) loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // After OAuth callback, the user lands back on /host?spotify=connected.
  // The HostDashboard reads that query and could re-open settings; in any
  // case when this dialog is reopened it will refetch status. We also poll
  // once after focus to catch the same-tab redirect case.
  useEffect(() => {
    const onFocus = () => loadStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const connect = async () => {
    try {
      const { data } = await spotifyApi.startLogin(eventId, window.location.origin);
      if (data?.auth_url) {
        // Use full-page redirect: Spotify blocks popups in many browsers,
        // and our OAuth callback redirects back to /host afterwards.
        window.location.href = data.auth_url;
      }
    } catch (e) {
      toast.error("Nao foi possivel iniciar o login do Spotify.");
    }
  };

  const openPicker = async () => {
    setPicking(true);
    setLoadingPl(true);
    try {
      const { data } = await spotifyApi.playlists(eventId);
      setPlaylists(data?.playlists || []);
    } catch (e) {
      toast.error("Nao foi possivel carregar suas playlists.");
      setPicking(false);
    } finally {
      setLoadingPl(false);
    }
  };

  const choose = async (pl) => {
    try {
      await spotifyApi.selectPlaylist(eventId, pl);
      toast.success(`Playlist "${pl.name}" selecionada!`);
      setPicking(false);
      await loadStatus();
    } catch (e) {
      toast.error("Erro ao salvar a playlist.");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Desconectar o Spotify deste evento?")) return;
    try {
      await spotifyApi.disconnect(eventId);
      toast.success("Spotify desconectado.");
      await loadStatus();
    } catch (e) {
      toast.error("Erro ao desconectar.");
    }
  };

  if (!status) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-[#1DB954]" />
      </div>
    );
  }

  // Not connected → show CTA
  if (!status.connected) {
    return (
      <div className="bg-[#1DB954] border-2 border-[#1C1917] rounded-xl p-4 shadow-[3px_3px_0px_#1C1917]" data-testid="spotify-disconnected-card">
        <div className="flex items-center gap-2 mb-2">
          <SpotifyLogo />
          <p className="text-white font-black text-sm tracking-tight">Conectar Spotify</p>
        </div>
        <p className="text-white/90 text-[11px] leading-snug mb-3">
          Toque suas playlists do Spotify no Modo TV. Requer Spotify Premium.
        </p>
        <button
          onClick={connect}
          className="w-full bg-white text-[#1DB954] border-2 border-[#1C1917] rounded-full font-black text-sm py-2.5 shadow-[2px_2px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] transition-all flex items-center justify-center gap-2"
          data-testid="spotify-connect-btn"
        >
          <SpotifyLogo small />
          Entrar com Spotify
        </button>
      </div>
    );
  }

  // Connected
  return (
    <div className="bg-white border-2 border-[#1C1917] rounded-xl p-3 shadow-[3px_3px_0px_#1C1917] space-y-2" data-testid="spotify-connected-card">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-[#1DB954] border-2 border-[#1C1917] rounded-full flex items-center justify-center shrink-0">
          <SpotifyLogo small white />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-black text-[#1C1917] truncate">
            {status.user_name}
          </p>
          <p className="text-[10px] flex items-center gap-1">
            {status.is_premium ? (
              <span className="inline-flex items-center gap-1 text-[#B45309] font-bold">
                <Crown className="w-3 h-3" strokeWidth={3} /> Premium
              </span>
            ) : (
              <span className="text-red-600 font-bold">Conta gratis (precisa Premium)</span>
            )}
          </p>
        </div>
        <button
          onClick={disconnect}
          className="text-[10px] font-bold text-[#78716C] hover:text-red-600 underline"
          data-testid="spotify-disconnect-btn"
        >
          Sair
        </button>
      </div>

      {/* Selected playlist */}
      {status.playlist_uri ? (
        <div className="flex items-center gap-2 bg-[#F5F5F4] border-2 border-[#1C1917] rounded-lg p-2">
          {status.playlist_image ? (
            <img
              src={status.playlist_image}
              alt={status.playlist_name}
              className="w-10 h-10 rounded border-2 border-[#1C1917] object-cover shrink-0"
            />
          ) : (
            <div className="w-10 h-10 bg-[#1DB954] border-2 border-[#1C1917] rounded flex items-center justify-center shrink-0">
              <MusicIcon className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-[#78716C] uppercase font-bold tracking-wider">Playlist do Modo TV</p>
            <p className="text-[12px] font-bold text-[#1C1917] truncate">{status.playlist_name}</p>
          </div>
          <button
            onClick={openPicker}
            className="text-[10px] font-bold bg-[#1C1917] text-white px-2 py-1 rounded-full hover:bg-[#FF6B6B] transition-colors"
            data-testid="spotify-change-playlist-btn"
          >
            Trocar
          </button>
        </div>
      ) : (
        <button
          onClick={openPicker}
          className="w-full bg-[#1DB954] text-white border-2 border-[#1C1917] rounded-full font-bold text-sm py-2 shadow-[2px_2px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] transition-all flex items-center justify-center gap-2"
          data-testid="spotify-pick-playlist-btn"
        >
          <MusicIcon className="w-4 h-4" strokeWidth={2.5} />
          Escolher uma playlist
        </button>
      )}

      {/* Playlist picker modal-ish */}
      {picking && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPicking(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border-2 border-[#1C1917] rounded-xl shadow-[6px_6px_0px_#1C1917] max-w-md w-full max-h-[80vh] flex flex-col"
            data-testid="spotify-playlist-picker"
          >
            <div className="flex items-center justify-between p-3 border-b-2 border-[#1C1917]">
              <p className="font-black text-sm">Suas playlists Spotify</p>
              <button onClick={() => setPicking(false)} className="w-7 h-7 flex items-center justify-center bg-[#1C1917] text-white rounded-lg">
                <X className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>
            <div className="overflow-y-auto p-2 space-y-1.5">
              {loadingPl ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-[#1DB954]" />
                </div>
              ) : playlists.length === 0 ? (
                <p className="text-center text-sm text-[#78716C] py-8">Nenhuma playlist encontrada na sua conta.</p>
              ) : (
                playlists.map((pl) => {
                  const selected = status.playlist_uri === pl.uri;
                  return (
                    <button
                      key={pl.uri}
                      onClick={() => choose(pl)}
                      className={`w-full flex items-center gap-2 p-2 rounded-lg border-2 border-[#1C1917] text-left hover:bg-[#FFFBF7] transition-colors ${
                        selected ? "bg-[#1DB954]/20" : "bg-white"
                      }`}
                      data-testid={`spotify-playlist-${pl.uri}`}
                    >
                      {pl.image ? (
                        <img src={pl.image} alt={pl.name} className="w-10 h-10 rounded border border-[#1C1917] object-cover shrink-0" />
                      ) : (
                        <div className="w-10 h-10 bg-[#1DB954] border border-[#1C1917] rounded flex items-center justify-center shrink-0">
                          <MusicIcon className="w-4 h-4 text-white" strokeWidth={2.5} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-bold text-[#1C1917] truncate">{pl.name}</p>
                        <p className="text-[10px] text-[#78716C]">{pl.tracks ?? 0} faixas{pl.owner ? ` • ${pl.owner}` : ""}</p>
                      </div>
                      {selected && <Check className="w-4 h-4 text-[#1DB954] shrink-0" strokeWidth={3} />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SpotifyLogo({ small, white }) {
  const size = small ? 16 : 20;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={white ? "#fff" : "currentColor"} aria-hidden="true">
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.708 17.291c-.232.378-.728.499-1.106.267-3.027-1.85-6.836-2.268-11.328-1.245-.43.098-.857-.171-.955-.6-.098-.43.17-.857.6-.955 4.917-1.119 9.13-.638 12.532 1.43.378.232.499.728.267 1.103zm1.526-3.391c-.291.473-.91.621-1.382.331-3.466-2.131-8.752-2.748-12.853-1.502-.531.16-1.094-.139-1.255-.67-.16-.531.139-1.094.67-1.255 4.694-1.426 10.529-.736 14.487 1.703.473.292.622.91.333 1.393zm.131-3.534C15.211 7.821 8.523 7.572 4.674 8.768c-.638.198-1.314-.156-1.512-.794-.198-.638.156-1.314.795-1.512 4.426-1.376 11.823-1.082 16.47 1.696.575.343.762 1.085.42 1.66-.343.574-1.085.762-1.66.42z" />
    </svg>
  );
}


// =================================================================
// LIVE TV (YouTube)
// Splits the TV mode page in two: 70% slideshow / 30% YouTube live.
// Audio modes: spotify (default), youtube, muted.
// =================================================================
function LiveTvPanel({ eventId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [url, setUrl] = useState("");
  const [audioMode, setAudioMode] = useState("spotify");

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    eventConfigApi
      .getLiveTv(eventId)
      .then(({ data }) => {
        setEnabled(!!data?.enabled);
        setUrl(data?.url || "");
        setAudioMode(data?.audio_mode || "spotify");
      })
      .catch(() => toast.error("Erro ao carregar TV ao vivo."))
      .finally(() => setLoading(false));
  }, [eventId]);

  const save = async () => {
    setSaving(true);
    try {
      await eventConfigApi.updateLiveTv(eventId, {
        enabled,
        url: url.trim(),
        audio_mode: audioMode,
      });
      toast.success(enabled ? "TV ao vivo ativada!" : "TV ao vivo desligada.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Nao foi possivel salvar.";
      toast.error(typeof msg === "string" ? msg : "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-[#FF6B6B]" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toggle */}
      <div className="flex items-center justify-between bg-white border-2 border-[#1C1917] rounded-xl p-3 shadow-[3px_3px_0px_#1C1917]">
        <div className="flex items-center gap-2">
          <Tv className="w-5 h-5 text-[#FF6B6B]" strokeWidth={2.5} />
          <div>
            <p className="text-sm font-black text-[#1C1917]">Ativar TV ao vivo</p>
            <p className="text-[10px] text-[#78716C]">Divide a tela: 50% slideshow / 50% transmissao YouTube</p>
          </div>
        </div>
        <button
          onClick={() => setEnabled((v) => !v)}
          className={`relative w-11 h-6 rounded-full border-2 border-[#1C1917] transition-colors ${
            enabled ? "bg-[#FF6B6B]" : "bg-white"
          }`}
          data-testid="livetv-enable-toggle"
        >
          <span
            className={`absolute top-0.5 w-4 h-4 bg-[#1C1917] rounded-full transition-transform ${
              enabled ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>

      {/* URL field */}
      <div className="space-y-1">
        <label className="text-[11px] font-bold text-[#78716C] uppercase tracking-wider">
          Link da transmissao ao vivo (YouTube ou Twitch)
        </label>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.youtube.com/live/abc... ou https://twitch.tv/canal"
          className="w-full bg-white border-2 border-[#1C1917] rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#FF6B6B]"
          data-testid="livetv-url-input"
        />
        <p className="text-[10px] text-[#78716C] leading-relaxed">
          <strong>YouTube:</strong> <code className="bg-[#F5F5F4] px-1 rounded">/live/ID</code>,{" "}
          <code className="bg-[#F5F5F4] px-1 rounded">watch?v=ID</code>{" "}
          ou <code className="bg-[#F5F5F4] px-1 rounded">youtu.be/ID</code><br/>
          <strong>Twitch:</strong> <code className="bg-[#F5F5F4] px-1 rounded">twitch.tv/canal</code>{" "}
          (ideal para lives de DJ ou esportes)
        </p>
      </div>

      {/* Audio mode */}
      <div className="space-y-1">
        <label className="text-[11px] font-bold text-[#78716C] uppercase tracking-wider">
          Som durante a TV ao vivo
        </label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { id: "spotify", label: "Spotify/MP3" },
            { id: "youtube", label: "TV ao vivo" },
            { id: "muted", label: "Sem som" },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setAudioMode(opt.id)}
              className={`text-[11px] font-bold py-2 rounded-lg border-2 border-[#1C1917] transition-all ${
                audioMode === opt.id
                  ? "bg-[#1C1917] text-white shadow-[2px_2px_0px_#FF6B6B]"
                  : "bg-white text-[#1C1917] hover:bg-[#FFFBF7]"
              }`}
              data-testid={`livetv-audio-${opt.id}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Spotify warning */}
      {enabled && audioMode === "youtube" && (
        <div className="flex items-start gap-2 bg-[#FEF3C7] border-2 border-[#1C1917] rounded-lg p-3 shadow-[2px_2px_0px_#1C1917]">
          <AlertTriangle className="w-4 h-4 text-[#B45309] shrink-0 mt-0.5" strokeWidth={2.5} />
          <p className="text-[11px] text-[#78716C] leading-snug">
            Com som no YouTube, o <span className="font-bold text-[#1C1917]">Spotify/MP3 ficara em pausa</span>{" "}
            durante a TV ao vivo. A musica volta automaticamente quando voce desligar a TV ao vivo.
          </p>
        </div>
      )}

      {enabled && audioMode === "muted" && (
        <div className="flex items-start gap-2 bg-[#FEF3C7] border-2 border-[#1C1917] rounded-lg p-3 shadow-[2px_2px_0px_#1C1917]">
          <AlertTriangle className="w-4 h-4 text-[#B45309] shrink-0 mt-0.5" strokeWidth={2.5} />
          <p className="text-[11px] text-[#78716C] leading-snug">
            Modo silencioso: nem o YouTube nem o Spotify/MP3 vao tocar enquanto a TV ao vivo estiver ativa.
          </p>
        </div>
      )}

      {/* Save */}
      <button
        onClick={save}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all disabled:opacity-60"
        data-testid="livetv-save-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2.5} />}
        Salvar TV ao vivo
      </button>
    </div>
  );
}

function TVDevicesPanel({ eventId }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await tvApi.listDevices(eventId);
      setDevices(data?.devices || []);
    } catch (e) {
      toast.error("Erro ao carregar TVs conectadas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (eventId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [eventId]);

  const handleRevoke = async (tokenId, label) => {
    if (!window.confirm(`Desconectar a TV "${label}"? Ela vai precisar parear novamente para exibir o evento.`)) return;
    setRevokingId(tokenId);
    try {
      await tvApi.revokeDevice(eventId, tokenId);
      toast.success("TV desconectada com sucesso");
      load();
    } catch (e) {
      toast.error("Erro ao desconectar TV");
    } finally {
      setRevokingId(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-4 py-4" data-testid="tv-devices-panel">
      <div>
        <p className="text-xs text-[#78716C] mb-3">
          TVs que estao com permissao para exibir este evento em modo slideshow.
          Desconecte aqui se nao usar mais aquele dispositivo.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-[#FF6B6B]" />
        </div>
      ) : devices.length === 0 ? (
        <div className="text-center py-8 px-4 border-2 border-dashed border-[#E7E5E4] rounded-xl">
          <Tv className="w-10 h-10 text-[#A8A29E] mx-auto mb-2" strokeWidth={2} />
          <p className="font-bold text-[#1C1917] mb-1">Nenhuma TV conectada ainda</p>
          <p className="text-xs text-[#78716C]">
            Abra <code className="bg-[#F5F5F4] px-1 rounded">festaia.com.br/tv</code> na TV
            e use o botao "Conectar TV" no dashboard.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {devices.map((d) => (
            <div
              key={d.token_id}
              className="flex items-center gap-3 p-3 border-2 border-[#1C1917] rounded-xl shadow-[2px_2px_0px_#1C1917] bg-white"
              data-testid={`tv-device-${d.token_id}`}
            >
              <div className="w-9 h-9 bg-[#FF6B6B] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shrink-0">
                <Tv className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-[#1C1917] truncate">{d.device_label}</p>
                <p className="text-[11px] text-[#78716C]">
                  ID #{d.token_id} - desde {fmtDate(d.created_at)}
                </p>
              </div>
              <button
                onClick={() => handleRevoke(d.token_id, d.device_label)}
                disabled={revokingId === d.token_id}
                className="shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-white text-red-600 border-2 border-red-600 rounded-full shadow-[2px_2px_0px_#DC2626] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#DC2626] active:translate-y-[2px] active:shadow-none transition-all disabled:opacity-50"
                data-testid={`tv-device-revoke-${d.token_id}`}
              >
                {revokingId === d.token_id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                )}
                Desconectar
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


